// « Parcours d'un ticket » — the eight steps every ticket goes through, in the
// order the platform runs them, with what this account has set up for each:
// a missing piece is one click from the page that sets it up, the reception
// stage is switched on right here, and every step says whether an AI model is
// involved. Backend: GET /api/setup/journey (app/services/ticket_journey_service.py),
// PUT /api/automation/ticket-workflow/reception and /reading (the choices of
// step 2: subject rewrite, Envoi du Net theme), /wakeup (step 8: who follows
// up — timers, agent in pilot, agent). Spec: docs/ticket-journey.md.

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    FaArrowRight, FaCheck, FaExclamation, FaInbox, FaMagic, FaRegEye, FaIdBadge, FaDatabase,
    FaTruck, FaRobot, FaHandshake, FaFlagCheckered,
} from 'react-icons/fa';
import './Journey.css';

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

export const STEP_ICONS = {
    reception: FaInbox, reading: FaRegEye, identification: FaIdBadge, enrichment: FaDatabase,
    tracking: FaTruck, agent: FaRobot, carrier: FaHandshake, followup: FaFlagCheckered,
};

// French defaults; the locale files carry the translations.
const STEP_TEXT = {
    reception: ['Réception', 'Le message arrive par un de vos canaux : e-mail, chatbot, formulaire, WhatsApp ou Messenger, ou l’API de vos clients B2B.'],
    reading: ['Lecture du message', 'L’IA lit chaque message : motif, langue, urgence, et les numéros cités (suivi, commande, facture).'],
    identification: ['Identification du client', 'Le ticket est rattaché à votre client B2B d’après l’adresse de l’expéditeur (adresse complète, puis domaine). Ses consignes et ses règles d’envoi s’appliquent alors.'],
    enrichment: ['Enrichissement', 'Vos systèmes complètent le ticket : commande, produits, colis, adresses. Ils sont interrogés avec les numéros lus à l’étape 2, l’adresse du client et le client B2B.'],
    tracking: ['Suivi du colis', 'Le suivi transporteur est lu à l’arrivée des e-mails, puis toutes les 12 h pendant 45 jours pour chaque ticket qui a un numéro de suivi. Une livraison ou un colis qui repart est signalé à l’agent, qui peut aussi relire le suivi à tout moment.'],
    agent: ['Réponse de l’agent IA', 'L’agent rédige la réponse avec tout ce qui précède, et peut interroger vos sources ou le transporteur pendant sa réponse (outils). Les règles d’automatisation décident si la réponse part seule ou attend votre validation.'],
    carrier: ['Contact transporteur', 'Quand il faut agir, l’agent ouvre la demande sur le portail du transporteur (avec vos identifiants) ou lui écrit par e-mail, puis suit ses réponses.'],
    followup: ['Relances et clôture', 'Relance des dossiers sans réponse — par les minuteries ou par l’agent IA lui-même —, statut du ticket, justification de la résolution ; vos destinataires sont informés par e-mail, SMS et la page de suivi.'],
};

const AI_TEXT = {
    reception: 'Sans IA.',
    reading: 'Un appel IA par message en texte libre (e-mail, chat, WhatsApp). Le formulaire, l’API de vos clients, Envoi du Net et Spacefill donnent déjà ces numéros dans des champs : rien à extraire. L’agent routeur (IA) choisit ensuite l’agent qui répond. Selon vos choix ci-dessous, l’IA réécrit aussi l’objet en français et choisit le thème Envoi du Net.',
    identification: 'Sans IA : correspondance d’adresses. L’API de vos clients (clé du client), Envoi du Net et Spacefill donnent déjà le client.',
    enrichment: 'Sans IA à la lecture : les champs sont repris tels que votre système les donne. L’IA sert seulement à proposer la correspondance des champs quand vous ajoutez une source, et à réparer la lecture d’un portail web si la page change.',
    tracking: 'Sans IA.',
    agent: 'Appels IA : la réponse, puis le contrôle avant envoi.',
    carrier: 'L’agent (IA) décide et rédige ; le remplissage du portail est automatisé.',
    followup: 'Appels IA : choix du statut et justification de la résolution. Quand l’agent relance lui-même, chacun de ses réveils est un traitement de l’agent.',
};

const STATUS_TEXT = {
    ready: 'Prêt', auto: 'Automatique', todo: 'À compléter', warning: 'À vérifier', recommended: 'Recommandé',
};

const ITEM_TEXT = {
    mailbox: 'Boîte e-mail connectée',
    chatbot: 'Chatbot de votre site',
    web_form: 'Formulaire de tickets',
    messaging: 'WhatsApp, Messenger, Instagram',
    voice: 'Téléphone (agent vocal)',
    erp_api: 'API pour vos clients B2B',
    edn: 'Tickets Envoi du Net',
    spacefill: 'Incidents Spacefill',
    intake: 'Lecture automatique de chaque message',
    subjects: 'Votre liste de motifs',
    topics: 'Classification par sujet',
    router: 'Consignes de l’agent routeur',
    subject_translation: 'Objet des tickets (e-mails Outlook, formulaire)',
    edn_topic: 'Thème Envoi du Net',
    clients: 'Clients B2B reconnaissables (adresse ou domaine)',
    reception_identify: 'Reconnaître le client sur tous les canaux',
    sources: 'Sources de données',
    reception_sources: 'Lire vos sources à l’arrivée, sur tous les canaux',
    failing: 'Sources en échec',
    verification: 'Sources en période de vérification',
    provider: 'Service de suivi',
    refresh: 'Relecture toutes les 12 h pendant 45 jours',
    agent: 'Agent IA actif',
    tools: 'Outils de l’agent',
    automation: 'Envoi automatique des réponses',
    status_instructions: 'Consignes de statut',
    carriers: 'Transporteurs configurés',
    sending: 'Envoi des e-mails aux transporteurs',
    credentials: 'Identifiants de portail manquants',
    agent_wakeup: 'Qui relance les dossiers sans réponse',
    followup_rules: 'Relances des transporteurs',
    notifications: 'Modèles de notifications',
    tracking_page: 'Page de suivi',
    sms: 'SMS aux destinataires',
    parcel_monitor: 'Surveillance des colis',
};

// Choices of the settings switched in place (``select:<field>``).
const SELECT_CHOICES = {
    translate_subject: [
        ['translate', 'Réécrit en français par l’IA'],
        ['keep', 'Gardé tel que l’expéditeur l’a écrit'],
    ],
    wakeup_mode: [
        ['off', 'Les minuteries (règles de relance)'],
        ['pilot', 'Pilote : l’agent prépare ses relances en brouillon, les minuteries continuent'],
        ['live', 'L’agent IA, qui décide lui-même quand relancer'],
    ],
    edn_topic: [
        ['creation', 'Choisi par l’IA à la création du ticket'],
        ['resolution', 'Choisi par l’IA à la résolution du ticket'],
        ['sender', 'Celui que l’expéditeur a choisi dans Envoi du Net'],
        ['fixed', 'Toujours le même thème'],
    ],
};

const JourneySetting = ({ item, t, canEdit, busy, onSave }) => {
    const field = item.action.slice('select:'.length);
    const facts = item.facts || {};
    const [pendingFixed, setPendingFixed] = useState(false);
    const choices = SELECT_CHOICES[field] || [];
    const value = pendingFixed ? 'fixed' : facts.value;
    const change = (next) => {
        if (field === 'wakeup_mode') {
            onSave({ mode: next }, 'wakeup');
        } else if (field === 'translate_subject') {
            onSave({ translate_subject: next === 'translate' });
        } else if (next === 'fixed' && !facts.topic_id) {
            setPendingFixed(true); // saved once a theme is picked
        } else {
            setPendingFixed(false);
            onSave(next === 'fixed' ? { edn_topic: 'fixed', edn_topic_id: facts.topic_id } : { edn_topic: next });
        }
    };
    return (
        <div className="tj-setting">
            <select className="tj-select" value={value} disabled={!canEdit || busy} onChange={(e) => change(e.target.value)}
                aria-label={t(`journey.items.${item.key}`, ITEM_TEXT[item.key] || item.key)}>
                {choices.map(([key, label]) => (
                    <option key={key} value={key}>{t(`journey.choices.${field}.${key}`, label)}</option>
                ))}
            </select>
            {field === 'edn_topic' && value === 'fixed' && (
                <select className="tj-select" value={facts.topic_id || ''} disabled={!canEdit || busy}
                    aria-label={t('journey.choices.edn_topic.pick', 'Thème à appliquer')}
                    onChange={(e) => e.target.value && onSave({ edn_topic: 'fixed', edn_topic_id: Number(e.target.value) })}>
                    <option value="" disabled>{t('journey.choices.edn_topic.pick', 'Thème à appliquer')}</option>
                    {(facts.topics || []).map((topic) => (
                        <option key={topic.id} value={topic.id}>{topic.label}</option>
                    ))}
                </select>
            )}
        </div>
    );
};

const itemDetail = (item, t) => {
    const f = item.facts || {};
    switch (item.key) {
        case 'mailbox':
            if (f.reconnect) return t('journey.detail.reconnect', '{{n}} boîte(s) Outlook à reconnecter : leurs e-mails n’arrivent plus.', { n: f.reconnect });
            if (item.state === 'missing') return t('journey.detail.noMailbox', 'Connectez Gmail ou Outlook : c’est par là qu’arrive l’essentiel des demandes.');
            return [
                f.gmail === true ? 'Gmail' : f.gmail,
                f.outlook ? t('journey.detail.outlookCount', '{{n}} boîte(s) Outlook', { n: f.outlook }) : null,
            ].filter(Boolean).join(' · ');
        case 'messaging':
            return (f.connected || []).map((name) => name.charAt(0).toUpperCase() + name.slice(1)).join(', ');
        case 'voice':
            return f.numbers ? t('journey.detail.voiceNumbers', '{{n}} numéro(s)', { n: f.numbers }) : '';
        case 'erp_api':
            return f.clients ? t('journey.detail.erpClients', '{{n}} client(s) envoient leurs tickets par l’API', { n: f.clients }) : t('journey.detail.erpHint', 'Une clé par client B2B, sur sa fiche.');
        case 'router':
            return item.state === 'missing' ? t('journey.detail.noRouter', 'Sans consignes, le formulaire et l’API ne reçoivent pas de réponse de l’IA.') : '';
        case 'subject_translation':
            return t('journey.detail.subjectTranslation', 'Le titre du ticket dans votre liste. Réécrit : un appel IA par message, titre court en français. Gardé : les mots de l’expéditeur. Les motifs de votre formulaire restent toujours tels quels.');
        case 'edn_topic':
            return t('journey.detail.ednTopic', 'Envoi du Net exige un thème pour résoudre un ticket. À la résolution : un appel IA seulement pour les tickets résolus. Thème de l’expéditeur : sans IA (l’IA le choisit à la résolution s’il manque).');
        case 'clients':
            if (item.state === 'warning') return t('journey.detail.clientsNoEmail', '{{total}} client(s), aucun avec une adresse ou un domaine : impossible de les reconnaître.', { total: f.total });
            if (item.state === 'missing') return t('journey.detail.noClients', 'Ajoutez vos clients B2B avec leurs adresses ou leur domaine.');
            return t('journey.detail.clientsCount', '{{with}} sur {{total}} avec une adresse ou un domaine', { with: f.with_emails, total: f.total });
        case 'reception_identify':
            return item.state === 'missing' ? t('journey.detail.identifyOff', 'Aujourd’hui le client n’est reconnu que par l’API de vos clients, Envoi du Net, Spacefill et les e-mails Outlook des clients EffiTrace.') : '';
        case 'sources':
            return item.state === 'missing' ? t('journey.detail.noSources', 'Votre WMS, votre ERP, vos boutiques… : l’agent répond avec la vraie commande.') : t('journey.detail.sourceCount', '{{n}} source(s)', { n: f.count });
        case 'reception_sources':
            return item.state === 'missing' ? t('journey.detail.sourcesOff', 'Aujourd’hui vos sources ne sont lues à l’arrivée que pour Outlook (clients EffiTrace), l’API de vos clients, Envoi du Net et Spacefill ; ailleurs, seulement si l’agent les interroge.') : '';
        case 'failing':
        case 'verification':
            return (f.keys || []).join(', ');
        case 'provider':
            return f.provider === 'edn' ? t('journey.detail.providerEdn', 'API Envoi du Net de votre compte') : t('journey.detail.providerShip24', 'Ship24, inclus');
        case 'agent':
            return f.name && f.name !== true ? f.name : (item.state === 'missing' ? t('journey.detail.noAgent', 'Aucun agent actif : les tickets n’ont pas de réponse IA.') : '');
        case 'tools':
            return item.state === 'missing' ? t('journey.detail.noTools', 'Sans outils, l’agent ne peut ni suivre un colis ni contacter un transporteur.') : t('journey.detail.toolCount', '{{n}} outil(s)', { n: f.count });
        case 'automation':
            return item.state === 'ok' ? t('journey.detail.automationOn', 'Les réponses validées par le contrôle partent seules.') : t('journey.detail.automationOff', 'Les réponses restent en brouillon jusqu’à votre validation.');
        case 'carriers':
            return item.state === 'missing' ? t('journey.detail.noCarriers', 'Ajoutez les transporteurs avec qui vous travaillez.') : t('journey.detail.carrierCount', '{{n}} transporteur(s)', { n: f.count });
        case 'sending':
            if (f.via === 'account_mailbox') {
                return t('journey.detail.viaAccountMailbox', 'Depuis la boîte Gmail du compte, qui reçoit aussi leurs réponses.');
            }
            return t('journey.detail.viaPlatform', 'Depuis l’adresse de la plateforme, qui reçoit aussi leurs réponses. Connectez la boîte Gmail du compte dans Paramètres pour écrire avec votre propre adresse.');
        case 'credentials':
            return t('journey.detail.credentials', '{{carriers}} : contactés par e-mail tant que leurs identifiants manquent.', { carriers: (f.carriers || []).join(', ') });
        case 'agent_wakeup':
            return f.value === 'live'
                ? t('journey.detail.wakeupLive', 'Sur l’e-mail, les formulaires, l’API et le ticketing externe, l’agent revient sur chaque dossier quand il l’a décidé ; les minuteries ne relancent plus ces tickets.')
                : t('journey.detail.wakeupOther', 'Le détail, les règles de la plateforme et vos délais : page Relances.');
        case 'followup_rules':
            return item.state === 'ok' ? t('journey.detail.followupCount', '{{n}} règle(s)', { n: f.count }) : t('journey.detail.followupDefault', 'Par défaut : relance après 72 h.');
        default:
            return '';
    }
};

const StateIcon = ({ state }) => {
    if (state === 'ok') return <span className="tj-mark tj-mark--ok" aria-hidden="true"><FaCheck /></span>;
    if (state === 'missing' || state === 'warning') return <span className={`tj-mark tj-mark--${state}`} aria-hidden="true"><FaExclamation /></span>;
    return <span className="tj-mark tj-mark--optional" aria-hidden="true" />;
};

const JourneyItem = ({ item, t, canEdit, busy, onToggle, onSave }) => {
    const detail = itemDetail(item, t);
    const setting = item.action && item.action.startsWith('select:');
    const toggle = item.action && item.action.startsWith('toggle:') ? item.action.slice(7) : null;
    let action = null;
    if (toggle && canEdit && item.state === 'missing') {
        action = (
            <button type="button" className="u-btn u-btn--secondary tj-item-action" disabled={busy} onClick={() => onToggle(toggle)}>
                {t('journey.enable', 'Activer')}
            </button>
        );
    } else if (toggle && canEdit && item.state === 'ok') {
        action = (
            <button type="button" className="tj-link" disabled={busy} onClick={() => onToggle(toggle, false)}>
                {t('journey.disable', 'Désactiver')}
            </button>
        );
    } else if (item.route && item.level !== 'info') {
        action = (
            <Link className="tj-link" to={item.route}>
                {item.state === 'missing' || item.state === 'warning' ? t('journey.configure', 'Configurer') : t('journey.open', 'Ouvrir')}
                <FaArrowRight aria-hidden="true" />
            </Link>
        );
    }
    return (
        <li className={`tj-item tj-item--${item.state}`}>
            <StateIcon state={item.state} />
            <div className="tj-item-text">
                <strong>
                    {t(`journey.items.${item.key}`, ITEM_TEXT[item.key] || item.key)}
                    {item.level === 'required' && item.state === 'missing' && <em className="tj-tag tj-tag--danger">{t('journey.required', 'requis')}</em>}
                    {item.level === 'optional' && item.state === 'optional' && <em className="tj-tag">{t('journey.optional', 'facultatif')}</em>}
                </strong>
                {detail && <span>{detail}</span>}
                {setting && <JourneySetting item={item} t={t} canEdit={canEdit} busy={busy} onSave={onSave} />}
            </div>
            {!setting && action}
        </li>
    );
};

const TicketJourney = ({ journey, canEdit = true, onChanged, initialStep }) => {
    const { t } = useTranslation();
    const steps = journey?.steps || [];
    const [selected, setSelected] = useState(initialStep || journey?.next_step || 'reception');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (initialStep) setSelected(initialStep);
    }, [initialStep]);

    const toggle = async (field, on = true) => {
        setBusy(true);
        setError('');
        try {
            await axios.put('/api/automation/ticket-workflow/reception', { [field]: on }, authHeaders());
            await onChanged?.();
        } catch (e) {
            setError(t('journey.toggleError', 'Le réglage n’a pas pu être enregistré.'));
        } finally {
            setBusy(false);
        }
    };

    const saveSetting = async (patch, target = 'reading') => {
        setBusy(true);
        setError('');
        try {
            await axios.put(`/api/automation/ticket-workflow/${target}`, patch, authHeaders());
            await onChanged?.();
        } catch (e) {
            setError(e?.response?.data?.detail || t('journey.toggleError', 'Le réglage n’a pas pu être enregistré.'));
        } finally {
            setBusy(false);
        }
    };

    const current = steps.find((s) => s.key === selected) || steps.find((s) => s.key === journey?.next_step) || steps[0];
    if (!current) return null;
    const index = steps.indexOf(current);
    const [title, what] = STEP_TEXT[current.key] || [current.key, ''];

    return (
        <section className="tj" aria-label={t('journey.title', 'Parcours d’un ticket')}>
            <header className="tj-head">
                <div>
                    <h2>{t('journey.title', 'Parcours d’un ticket')}</h2>
                    <p>{t('journey.subtitle', 'Ce qui arrive à chaque ticket, dans l’ordre, et ce qui reste à régler sur votre compte. Cliquez sur une étape.')}</p>
                </div>
                <div className="tj-progress" aria-label={t('journey.progressLabel', 'Étapes prêtes')}>
                    <strong>{journey.ready}/{journey.total}</strong>
                    <span>{t('journey.progress', 'étapes prêtes')}</span>
                    <span className="tj-meter"><span style={{ width: `${(journey.ready / journey.total) * 100}%` }} /></span>
                </div>
            </header>

            <ol className="tj-rail">
                {steps.map((step, i) => {
                    const Icon = STEP_ICONS[step.key] || FaInbox;
                    return (
                        <li key={step.key} className="tj-rail-item">
                            <button type="button" aria-current={step.key === current.key ? 'step' : undefined}
                                className={`tj-step tj-step--${step.status} ${step.key === current.key ? 'tj-step--selected' : ''}`}
                                onClick={() => setSelected(step.key)}>
                                <span className="tj-step-top" aria-hidden="true">
                                    <span className="tj-step-icon"><Icon /></span>
                                    <span className="tj-step-num">{i + 1}</span>
                                </span>
                                <span className="tj-step-name">{t(`journey.steps.${step.key}.title`, STEP_TEXT[step.key]?.[0] || step.key)}</span>
                                <span className="tj-step-status">
                                    {t(`journey.status.${step.status}`, STATUS_TEXT[step.status] || step.status)}
                                    {step.ai && <em className="tj-ai" title={t('journey.aiTitle', 'Un modèle d’IA intervient à cette étape')}>{t('journey.ai', 'IA')}</em>}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ol>

            <div className="tj-detail">
                <div className="tj-detail-head">
                    <h3>{index + 1}. {t(`journey.steps.${current.key}.title`, title)}</h3>
                    <span className={`tj-chip tj-chip--${current.status}`}>{t(`journey.status.${current.status}`, STATUS_TEXT[current.status] || current.status)}</span>
                </div>
                <p className="tj-what">{t(`journey.steps.${current.key}.what`, what)}</p>
                <p className={`tj-ai-note ${current.ai ? 'tj-ai-note--on' : ''}`}>
                    <FaMagic aria-hidden="true" /> {t(`journey.steps.${current.key}.ai`, AI_TEXT[current.key] || '')}
                </p>
                {error && <p className="tj-error" role="alert">{error}</p>}
                <ul className="tj-items">
                    {current.items.map((item) => (
                        <JourneyItem key={item.key} item={item} t={t} canEdit={canEdit} busy={busy} onToggle={toggle} onSave={saveSetting} />
                    ))}
                </ul>
            </div>
        </section>
    );
};

export default TicketJourney;
